import { Progress } from "@/components/ui/progress";
import {
  AccordionContent, AccordionItem, AccordionTrigger,
} from "@/components/ui/accordion";
import { Badge } from "@/components/ui/badge";
import { Globe, XCircle, AlertTriangle, Server, ShieldAlert } from "lucide-react";
import { SectionBadge } from "./shared";

export function WebsiteAuditSection({ company, wChecks, catGroups, fails, warns, passes }) {
  const blocked = company.website_blocked;
  if (!wChecks.length && !blocked) return null;
  return (
    <AccordionItem value="website-audit" className="border border-slate-200 rounded-lg shadow-sm overflow-hidden">
      <AccordionTrigger className="px-4 py-3 hover:no-underline hover:bg-slate-50 [&[data-state=open]]:bg-slate-50">
        <div className="flex items-center gap-2 flex-1">
          <Globe className="w-4 h-4 text-teal-600" />
          <span className="text-sm font-semibold" style={{ fontFamily: 'Manrope, sans-serif' }}>Website Audit</span>
          {blocked
            ? <span className="text-[10px] text-amber-600 font-medium ml-1">Protected — couldn't crawl</span>
            : <span className="text-[10px] text-muted-foreground ml-1">{wChecks.length} checks</span>}
          <div className="flex items-center gap-1.5 ml-auto mr-3">
            <SectionBadge count={fails.length} color="bg-rose-100 text-rose-700" />
            <SectionBadge count={warns.length} color="bg-amber-100 text-amber-700" />
            <SectionBadge count={passes.length} color="bg-emerald-100 text-emerald-700" />
          </div>
        </div>
      </AccordionTrigger>
      <AccordionContent className="px-4">
        {blocked && (
          <div className="flex items-start gap-2 mb-4 p-2.5 rounded-lg border border-amber-200 bg-amber-50/60" data-testid="website-blocked-banner">
            <ShieldAlert className="w-4 h-4 text-amber-600 shrink-0 mt-0.5" />
            <div className="min-w-0">
              <p className="text-[11px] font-semibold text-amber-800">Website is behind anti-bot protection</p>
              <p className="text-[10px] text-amber-700">
                {company.website_block_reason || "This site (Cloudflare/Sucuri/Imperva) requires a CAPTCHA, so it couldn't be auto-crawled."} Review it manually to complete the website audit.
              </p>
            </div>
          </div>
        )}
        {/* Website platform / CMS — tells the team if we can take over or must rebuild */}
        {!blocked && company.website_platform !== undefined && (() => {
          const plat = company.website_platform;
          const transferable = plat === "WordPress";
          return (
            <div className={`flex items-center gap-2 mb-4 p-2.5 rounded-lg border ${
              plat ? (transferable ? "border-emerald-200 bg-emerald-50/60" : "border-amber-200 bg-amber-50/60") : "border-slate-200 bg-slate-50/60"
            }`} data-testid="website-platform">
              <Server className={`w-4 h-4 shrink-0 ${plat ? (transferable ? "text-emerald-600" : "text-amber-600") : "text-slate-500"}`} />
              <div className="min-w-0 flex-1">
                <p className="text-[11px] font-semibold text-slate-800">
                  Built on: <span data-testid="platform-name">{plat || "Unknown / Custom"}</span>
                </p>
                <p className="text-[10px] text-slate-500">
                  {plat
                    ? (transferable
                        ? "WordPress — can be transferred in-house (hosting + maintenance takeover)."
                        : `${plat} is a proprietary platform — a rebuild into WordPress is typically required (RFP).`)
                    : "Platform couldn't be auto-detected — likely a custom/coded site; review manually."}
                </p>
              </div>
              <Badge variant="outline" className={`text-[8px] h-4 shrink-0 ${
                plat ? (transferable ? "bg-emerald-50 text-emerald-700 border-emerald-200" : "bg-amber-50 text-amber-700 border-amber-200") : "bg-slate-50 text-slate-500 border-slate-200"
              }`}>{plat ? (transferable ? "Can take over" : "Rebuild likely") : "Review"}</Badge>
            </div>
          );
        })()}
        {/* Category progress */}
        <div className="space-y-2.5 mb-4">
          {Object.entries(catGroups).map(([cat, checks]) => {
            const passed = checks.filter((c) => c.status === "pass").length;
            const pct = Math.round((passed / checks.length) * 100);
            return (
              <div key={cat}>
                <div className="flex items-center justify-between mb-1">
                  <span className="text-[11px] font-medium">{cat}</span>
                  <span className="text-[10px] text-muted-foreground">{passed}/{checks.length} pass ({pct}%)</span>
                </div>
                <Progress value={pct} className="h-1.5" />
              </div>
            );
          })}
        </div>

        {/* Issue list */}
        {fails.length > 0 && (
          <div className="mb-3">
            <p className="text-[10px] font-bold uppercase tracking-wider text-rose-600 mb-1.5">Critical ({fails.length})</p>
            <div className="space-y-1">
              {fails.map((c, i) => (
                <div key={i} className="flex items-start gap-2 p-2 rounded bg-rose-50/50 border border-rose-100">
                  <XCircle className="w-3 h-3 text-rose-500 mt-0.5 shrink-0" />
                  <div><p className="text-[11px] font-medium text-rose-800">{c.name} <span className="font-normal text-rose-600">— {c.recommendation}</span></p></div>
                </div>
              ))}
            </div>
          </div>
        )}
        {warns.length > 0 && (
          <div className="mb-3">
            <p className="text-[10px] font-bold uppercase tracking-wider text-amber-600 mb-1.5">Warnings ({warns.length})</p>
            <div className="space-y-1">
              {warns.map((c, i) => (
                <div key={i} className="flex items-start gap-2 p-2 rounded bg-amber-50/40 border border-amber-100">
                  <AlertTriangle className="w-3 h-3 text-amber-500 mt-0.5 shrink-0" />
                  <div><p className="text-[11px] font-medium text-amber-800">{c.name} <span className="font-normal text-amber-600">— {c.recommendation}</span></p></div>
                </div>
              ))}
            </div>
          </div>
        )}
        {passes.length > 0 && (
          <div>
            <p className="text-[10px] font-bold uppercase tracking-wider text-emerald-600 mb-1.5">Passing ({passes.length})</p>
            <div className="flex flex-wrap gap-1.5">
              {passes.map((c, i) => (
                <span key={i} className="text-[10px] px-2 py-0.5 rounded bg-emerald-50 text-emerald-700 border border-emerald-100">{c.name}</span>
              ))}
            </div>
          </div>
        )}
      </AccordionContent>
    </AccordionItem>
  );
}
